import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, CalendarCheck } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, NAV_LINKS } from "@/constants/site";
import { scrollToId } from "@/lib/scroll";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="bg-navy text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] sm:text-xs">
          <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-green-400" />
          <span>Same-Day Service Available Today — 7 Days a Week</span>
        </div>
      </div>

      <div
        className={`transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled
            ? "border-b border-slate-200/70 bg-white/85 shadow-lg shadow-slate-900/5 backdrop-blur-xl"
            : "border-b border-transparent bg-white/60 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2.5 sm:px-6 lg:px-8">
          <button
            data-testid="header-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex shrink-0 items-center"
            aria-label="GTA Appliance Rescue — back to top"
          >
            <img src="/logo.jpg" alt="GTA Appliance Rescue logo" className="h-12 w-auto sm:h-14" />
          </button>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="header-nav">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                data-testid={`nav-${link.id}`}
                onClick={() => go(link.id)}
                className="text-sm font-semibold text-slate-600 transition-colors duration-200 hover:text-rescue"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="hidden items-center gap-3 md:flex">
            <a
              data-testid="header-call-now-button"
              href={PHONE_TEL}
              className="group flex items-center gap-2 rounded-full bg-rescue px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-red-600/25 transition-[background-color,transform] duration-200 hover:bg-rescue-dark hover:-translate-y-0.5"
            >
              <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
              Call {PHONE_DISPLAY}
            </a>
            <button
              data-testid="header-request-service-button"
              onClick={() => go("request-service")}
              className="flex items-center gap-2 rounded-full border-2 border-navy px-5 py-2 text-sm font-bold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
            >
              <CalendarCheck className="h-4 w-4" />
              Request Service
            </button>
          </div>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="rounded-lg p-2 text-navy md:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden border-t border-slate-100 bg-white md:hidden"
              data-testid="mobile-menu"
            >
              <div className="space-y-1 px-4 py-4">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.id}
                    onClick={() => go(link.id)}
                    className="block w-full rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    {link.label}
                  </button>
                ))}
                <div className="flex gap-3 pt-3">
                  <a
                    href={PHONE_TEL}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full bg-rescue py-3 text-sm font-bold text-white"
                  >
                    <Phone className="h-4 w-4" /> Call Now
                  </a>
                  <button
                    onClick={() => go("request-service")}
                    className="flex-1 rounded-full border-2 border-navy py-3 text-sm font-bold text-navy"
                  >
                    Request Service
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
